import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Zap, Target, Layers, Check, Star, TrendingUp, Award, Users } from "lucide-react";
import api from "@/lib/api";
import ScriptCard from "@/components/ScriptCard";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import CustomRequestForm from "@/components/CustomRequestForm";

const FLOATING_CARDS = [
  { title: "Instagram Reel Script", subtitle: "Skincare • 15s", price: "₹499", img: "https://images.unsplash.com/photo-1664277497095-424e085175e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxjb250ZW50JTIwY3JlYXRvciUyMHZpZGVvfGVufDB8fHx8MTc5MDQ1MTI0N3ww&ixlib=rb-4.1.0&q=85" },
  { title: "YouTube Ad Script", subtitle: "Fitness • 30s", price: "₹699", img: "https://images.unsplash.com/photo-1630797160666-38e8c5ba44c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxjb250ZW50JTIwY3JlYXRvciUyMHZpZGVvfGVufDB8fHx8MTc5MDQ1MTI0N3ww&ixlib=rb-4.1.0&q=85" },
  { title: "UGC Script", subtitle: "EdTech • 45s", price: "₹799", img: "https://images.unsplash.com/photo-1673767298248-b128f17f89af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxjb250ZW50JTIwY3JlYXRvciUyMHZpZGVvfGVufDB8fHx8MTc5MDQ1MTI0N3ww&ixlib=rb-4.1.0&q=85" },
  { title: "Viral Hook Pack", subtitle: "Finance • 10 Hooks", price: "₹999", img: "https://images.unsplash.com/photo-1560785219-cc81ab373cd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxjb250ZW50JTIwY3JlYXRvciUyMHZpZGVvfGVufDB8fHx8MTc5MDQ1MTI0N3ww&ixlib=rb-4.1.0&q=85" },
];

const FLOAT_POS = [
  { top: "6%", left: "0%", delay: 0, rotate: -6 },
  { top: "22%", right: "0%", delay: 0.2, rotate: 5 },
  { top: "54%", left: "4%", delay: 0.4, rotate: 4 },
  { top: "62%", right: "6%", delay: 0.6, rotate: -3 },
];

export default function Home() {
  const [stats, setStats] = useState({ scripts: 12000, writers: 950, businesses: 3400, satisfaction: 98 });
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/scripts/featured").then(({ data }) => setFeatured(data)).catch(() => {});
  }, []);

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 pb-24 lg:pt-24 lg:pb-32 cw-noise">
        <div className="cw-orb" style={{ background: "#FF6B00", width: 500, height: 500, top: "-100px", left: "-150px" }} />
        <div className="cw-orb" style={{ background: "#FF8A2A", width: 400, height: 400, bottom: "-100px", right: "-100px", opacity: 0.25 }} />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-6">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="cw-label inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5">
                <Sparkles className="h-3 w-3 text-[#FF6B00]" /> India's #1 Script Marketplace
              </span>
              <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tighter">
                Turn <span className="cw-gradient-text">Words</span> Into Revenue.
              </h1>
              <p className="text-white/60 mt-6 text-lg max-w-xl leading-relaxed">
                Buy high-converting ad scripts from top Indian writers, or become a CreativeWayz Writer and earn by selling your creative ideas — 24/7.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/marketplace" className="cw-btn-primary inline-flex items-center gap-2" data-testid="hero-buy-scripts">
                  Buy Scripts <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/writers" className="cw-btn-ghost inline-flex items-center gap-2" data-testid="hero-become-writer">
                  Become a Writer
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-14">
                {[
                  { v: `${(stats.scripts/1000).toFixed(0)}k+`, l: "Scripts" },
                  { v: `${stats.writers}+`, l: "Writers" },
                  { v: `${(stats.businesses/1000).toFixed(1)}k+`, l: "Businesses" },
                  { v: `${stats.satisfaction}%`, l: "Satisfaction" },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="font-display text-2xl sm:text-3xl">{s.v}</div>
                    <div className="cw-label mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Floating cards */}
          <div className="lg:col-span-6 relative h-[520px] hidden lg:block">
            {FLOATING_CARDS.map((c, i) => (
              <motion.div
                key={i}
                className="absolute w-64 cw-glass rounded-2xl p-3 shadow-2xl"
                style={FLOAT_POS[i]}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: [0, -12, 0] }}
                transition={{
                  opacity: { duration: 0.6, delay: FLOAT_POS[i].delay },
                  y: { duration: 6 + i, repeat: Infinity, ease: "easeInOut", delay: FLOAT_POS[i].delay },
                }}
              >
                <div className="rounded-xl overflow-hidden h-24 mb-3">
                  <img src={c.img} alt={c.title} className="h-full w-full object-cover" style={{ transform: `rotate(${FLOAT_POS[i].rotate * 0}deg)` }} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-sm">{c.title}</div>
                    <div className="text-xs text-white/50 mt-0.5">{c.subtitle}</div>
                  </div>
                  <div className="text-[#FF6B00] font-display font-semibold">{c.price}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center">
          <div className="cw-label">The Playbook</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-3">How CreativeWayz Works</h2>
        </div>
        <div className="grid lg:grid-cols-2 gap-8 mt-16">
          <div className="cw-card rounded-3xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-full bg-[#FF6B00]/20 border border-[#FF6B00]/40 flex items-center justify-center">
                <Users className="h-5 w-5 text-[#FF6B00]" />
              </div>
              <div className="font-display text-xl">For Brands</div>
            </div>
            <ol className="space-y-4">
              {["Browse thousands of proven scripts","Purchase instantly with UPI or card","Download and use — full commercial rights","Or request a custom script from our writers"].map((t, i) => (
                <li key={i} className="flex gap-4">
                  <div className="cw-accent-text font-display font-bold w-8">0{i+1}</div>
                  <div className="text-white/80">{t}</div>
                </li>
              ))}
            </ol>
          </div>
          <div className="cw-card rounded-3xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-full bg-[#FF6B00]/20 border border-[#FF6B00]/40 flex items-center justify-center">
                <Award className="h-5 w-5 text-[#FF6B00]" />
              </div>
              <div className="font-display text-xl">For Writers</div>
            </div>
            <ol className="space-y-4">
              {["Subscribe monthly or yearly","Build your writer profile","Upload unlimited scripts","Earn 85–90% on every sale — forever"].map((t, i) => (
                <li key={i} className="flex gap-4">
                  <div className="cw-accent-text font-display font-bold w-8">0{i+1}</div>
                  <div className="text-white/80">{t}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* FEATURED MARKETPLACE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="cw-label">Featured Scripts</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-3">Scripts brands can't stop buying</h2>
          </div>
          <Link to="/marketplace" className="cw-btn-ghost inline-flex items-center gap-2" data-testid="home-browse-all">
            Browse all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((s, i) => (
            <ScriptCard key={s.script_id} script={s} index={i} />
          ))}
        </div>
      </section>

      {/* CUSTOM REQUEST */}
      <section id="custom" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="cw-label">Need Something Unique?</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-3 leading-tight">A custom script,<br /> written for <span className="cw-gradient-text">your brand</span>.</h2>
            <p className="text-white/60 mt-6 max-w-md">Tell us about your product, audience and goal. We'll match you with 3 hand-picked writers within 24 hours.</p>
            <ul className="mt-6 space-y-3">
              {["24-hour writer match","3 script variations","Free 1 round of revisions","Full commercial rights"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-white/80"><Check className="h-4 w-4 text-[#FF6B00]" /> {t}</li>
              ))}
            </ul>
          </div>
          <CustomRequestForm />
        </div>
      </section>

      {/* WHY SCRIPTS CONVERT — Bento */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center mb-14">
          <div className="cw-label">Why Our Scripts Convert</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-3">Built for the scroll. Written to sell.</h2>
        </div>
        <div className="grid md:grid-cols-6 gap-6">
          <BentoCard className="md:col-span-4" icon={<Zap />} title="Scroll-Stopping Hooks" body="Every script opens with a 3-second hook engineered from 12,000+ tested variations. Retention starts on frame one." big />
          <BentoCard className="md:col-span-2" icon={<Target />} title="Emotional Storytelling" body="Story arcs mapped to buyer psychology." />
          <BentoCard className="md:col-span-2" icon={<Layers />} title="Platform Optimized" body="Formats tuned to Reels, Shorts & YouTube." />
          <BentoCard className="md:col-span-4" icon={<TrendingUp />} title="Conversion-Focused CTAs" body="Every script closes with a CTA framework tested on real ad spend by top D2C brands in India." />
        </div>
      </section>

      {/* WRITER MEMBERSHIP */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 relative">
        <div className="cw-orb" style={{ background: "#FF6B00", width: 500, height: 500, top: "-100px", right: "-200px" }} />
        <div className="text-center mb-14 relative">
          <div className="cw-label">Writer Membership</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-3">Become a CreativeWayz Writer</h2>
          <p className="text-white/60 mt-4 max-w-2xl mx-auto">Sell unlimited scripts. Build your portfolio. Earn passive income while you sleep.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 relative">
          <PricingCard
            title="Monthly"
            price="₹999"
            period="/month"
            testid="pricing-monthly"
            features={["Upload unlimited scripts","Personal writer profile","Analytics dashboard","Keep 85% revenue","Priority support"]}
            cta="Start Monthly"
            plan="monthly"
          />
          <PricingCard
            highlighted
            title="Yearly"
            price="₹7,999"
            period="/year"
            testid="pricing-yearly"
            badge="Save 33%"
            features={["Everything in Monthly","Featured writer profile","Verified Writer badge","Homepage exposure","Keep 90% revenue","Early feature access"]}
            cta="Go Yearly"
            plan="yearly"
          />
        </div>
      </section>

      {/* COMPARISON */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="cw-card rounded-3xl overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/5">
                <th className="p-5 font-display text-white/60">Feature</th>
                <th className="p-5 font-display">Free Buyer</th>
                <th className="p-5 font-display">Writer Monthly</th>
                <th className="p-5 font-display text-[#FF6B00]">Writer Yearly</th>
              </tr>
            </thead>
            <tbody className="text-white/80">
              {[
                ["Buy Scripts","✓","✓","✓"],
                ["Sell Scripts","—","✓","✓"],
                ["Unlimited Uploads","—","✓","✓"],
                ["Analytics","—","✓","✓"],
                ["Featured Profile","—","—","✓"],
                ["Revenue Share","—","85%","90%"],
              ].map((row) => (
                <tr key={row[0]} className="border-b border-white/5 last:border-none">
                  {row.map((c, i) => <td key={i} className="p-5 text-sm">{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center mb-14">
          <div className="cw-label">Loved by 3,400+ brands</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-3">Words that made real money.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { name: "Ayesha Khan", role: "D2C Founder", quote: "CreativeWayz scripts improved our ad CTR from 1.1% to 3.4% in three weeks.", img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTA0NTEyNDZ8MA&ixlib=rb-4.1.0&q=85" },
            { name: "Rahul Verma", role: "Marketing Agency", quote: "Our team saves 12+ hours every week. The hook packs alone are worth the yearly plan.", img: "https://images.unsplash.com/photo-1506863530036-1efeddceb993?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTA0NTEyNDZ8MA&ixlib=rb-4.1.0&q=85" },
            { name: "Neha Patel", role: "UGC Creator", quote: "I recovered my subscription within the first week. Passive income while I sleep.", img: "https://images.unsplash.com/photo-1606143412458-acc5f86de897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTA0NTEyNDZ8MA&ixlib=rb-4.1.0&q=85" },
          ].map((t) => (
            <div key={t.name} className="cw-card rounded-2xl p-6">
              <div className="flex text-[#FF6B00] mb-4">{[...Array(5)].map((_,i)=><Star key={i} className="h-4 w-4 fill-[#FF6B00]" />)}</div>
              <p className="text-white/80 leading-relaxed">"{t.quote}"</p>
              <div className="flex items-center gap-3 mt-6">
                <img src={t.img} alt={t.name} className="h-10 w-10 rounded-full object-cover" />
                <div>
                  <div className="font-display">{t.name}</div>
                  <div className="text-xs text-white/50">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center mb-10">
          <div className="cw-label">FAQ</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-3">Answers, before you ask.</h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {[
            ["How do writers earn?","Every time a buyer purchases your script, 85% (Monthly) or 90% (Yearly) is credited to your CreativeWayz wallet. Payouts happen weekly via bank transfer or UPI."],
            ["How are scripts delivered?","Instantly. Once payment is confirmed, your script becomes available in your Buyer dashboard as text and downloadable PDF."],
            ["Can I request custom scripts?","Yes. Use the Custom Script section — we match you with 3 hand-picked writers within 24 hours."],
            ["Can I cancel my membership?","Absolutely. Cancel anytime from your dashboard. You keep access until the end of your billing cycle."],
            ["Do buyers own commercial rights?","Yes. Every purchased script includes full commercial rights for use across ads, organic content and campaigns."],
            ["How do payouts work?","Weekly payouts every Monday, minimum ₹500. Supported via UPI, bank transfer, and (soon) Stripe Connect for international creators."],
          ].map(([q,a], i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
              <AccordionTrigger className="font-display text-left text-lg hover:no-underline">{q}</AccordionTrigger>
              <AccordionContent className="text-white/70">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="cw-card rounded-3xl p-12 lg:p-20 relative overflow-hidden text-center">
          <div className="cw-orb" style={{ background: "#FF6B00", width: 400, height: 400, top: "-100px", left: "50%", marginLeft: "-200px" }} />
          <h2 className="font-display text-4xl sm:text-6xl leading-none relative">Start Creating.<br /><span className="cw-gradient-text">Start Selling.</span></h2>
          <div className="flex flex-wrap justify-center gap-4 mt-10 relative">
            <Link to="/marketplace" className="cw-btn-primary" data-testid="cta-buy-scripts">Buy Scripts</Link>
            <Link to="/writers" className="cw-btn-ghost" data-testid="cta-become-writer">Become a Writer</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function BentoCard({ className = "", icon, title, body, big = false }) {
  return (
    <div className={`cw-card rounded-2xl p-6 lg:p-8 ${className}`}>
      <div className="h-10 w-10 rounded-lg bg-[#FF6B00]/15 border border-[#FF6B00]/30 flex items-center justify-center text-[#FF6B00]">
        {icon}
      </div>
      <h3 className={`font-display mt-5 ${big ? "text-2xl" : "text-lg"}`}>{title}</h3>
      <p className="text-white/60 mt-2 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function PricingCard({ title, price, period, features, cta, highlighted, badge, plan, testid }) {
  const [loading, setLoading] = useState(false);
  const onClick = async () => {
    setLoading(true);
    try {
      const { toast } = await import("sonner");
      await api.post("/subscriptions/subscribe", { plan });
      toast.success(`You're subscribed to the ${plan} plan!`);
      window.location.href = "/dashboard/writer";
    } catch (e) {
      const { toast } = await import("sonner");
      if (e.response?.status === 401) {
        toast.error("Please log in as a writer first");
        window.location.href = "/signup?role=writer";
      } else {
        toast.error("Subscription failed");
      }
    } finally { setLoading(false); }
  };
  return (
    <div className={`relative rounded-3xl p-8 ${highlighted ? "bg-gradient-to-b from-[#1a0d05] to-[#0f0f0f] border border-[#FF6B00]/40 shadow-[0_0_60px_-20px_rgba(255,107,0,0.5)]" : "cw-card"}`}>
      {badge && (
        <span className="absolute -top-3 right-6 text-xs px-3 py-1 rounded-full bg-[#FF6B00] text-white font-semibold">{badge}</span>
      )}
      <div className="cw-label">{title}</div>
      <div className="mt-4 flex items-baseline gap-2">
        <div className="font-display text-5xl">{price}</div>
        <div className="text-white/50">{period}</div>
      </div>
      <ul className="mt-8 space-y-3">
        {features.map((f) => (
          <li key={f} className="flex items-center gap-3 text-white/80 text-sm">
            <Check className="h-4 w-4 text-[#FF6B00]" /> {f}
          </li>
        ))}
      </ul>
      <button
        onClick={onClick}
        disabled={loading}
        data-testid={testid}
        className={`mt-8 w-full ${highlighted ? "cw-btn-primary" : "cw-btn-ghost"}`}
      >{loading ? "Processing…" : cta}</button>
    </div>
  );
}
